import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import ConfirmationModal from '../ConfirmationModal';
import AlertDialog from '../AlertDialog';

interface Photo {
  id: string;
  image_url: string;
  caption: string | null;
  uploaded_by: string;
  created_at: string;
}

interface PhotoManagementProps {
  photos: Photo[];
  onDelete: (id: string, imageUrl: string) => Promise<void>;
}

const PhotoManagement: React.FC<PhotoManagementProps> = ({ photos, onDelete }) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState<Photo | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const handleDeleteClick = (photo: Photo) => {
    setPhotoToDelete(photo);
    setShowDeleteModal(true);
  };

  const handleDelete = async (id: string, imageUrl: string) => {
    try {
      await onDelete(id, imageUrl);
      setAlertMessage('Foto excluída com sucesso!');
      setShowAlert(true);
    } catch (error) {
      console.error('Error deleting photo:', error);
      setAlertMessage('Erro ao excluir foto. Por favor, tente novamente.');
      setShowAlert(true);
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-4 bg-wedding-beige-100 border-b">
          <h3 className="font-medium text-wedding-blue-800">Álbum de Fotos</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Foto
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Enviado por
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Legenda
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {photos.map((photo) => (
                <tr key={photo.id}>
                  <td className="px-6 py-4">
                    <img 
                      src={photo.image_url} 
                      alt="Foto do casamento"
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{photo.uploaded_by}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500">{photo.caption || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {new Date(photo.created_at).toLocaleDateString('pt-BR')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleDeleteClick(photo)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                      title="Excluir foto"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="p-4 border-t">
          <p className="text-sm text-gray-500">
            Total de fotos: <span className="font-medium">{photos.length}</span>
          </p>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Excluir Foto"
        message="Tem certeza que deseja excluir esta foto? Esta ação não pode ser desfeita."
        onClose={() => {
          setShowDeleteModal(false);
          setPhotoToDelete(null);
        }}
        onConfirm={() => {
          if (photoToDelete) {
            handleDelete(photoToDelete.id, photoToDelete.image_url);
            setShowDeleteModal(false);
            setPhotoToDelete(null);
          }
        }}
      />

      <AlertDialog
        isOpen={showAlert}
        title="Notificação"
        message={alertMessage}
        onClose={() => setShowAlert(false)}
      />
    </>
  );
};

export default PhotoManagement;